import React from 'react';

function formatDate(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Event card list. Clicking a card selects the event.
 *
 * Props:
 *   events       – array of { event_id, event_name, event_date, photos: [], videos: [] }
 *   selectedId   – id of currently selected event
 *   onSelect     – (event) => void
 *   loading      – bool
 */
export default function EventSelector({ events = [], selectedId, onSelect, loading = false }) {
  if (loading) {
    return (
      <div className="gallery-event-selector">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="gallery-event-card-skeleton" aria-hidden="true">
            <div className="skeleton skeleton-title" />
            <div className="skeleton skeleton-line" />
            <div className="skeleton skeleton-line short" />
          </div>
        ))}
      </div>
    );
  }

  if (!events.length) {
    return (
      <div className="gallery-event-selector-empty">
        <span style={{ fontSize: '2.5rem' }}>📅</span>
        <p>No events found for this year.</p>
      </div>
    );
  }

  return (
    <div className="gallery-event-selector" role="list" aria-label="Events for this year">
      {events.map((event) => {
        const isSelected =
          (event.event_id && event.event_id === selectedId) ||
          (!event.event_id && event.event_name === selectedId);
        // Use photos only so event cards never request video files as thumbnails.
        const thumbnail = event.photos?.[0]?.url;
        const hasVideoPlaceholder = !thumbnail && event.videos?.length > 0;
        return (
          <button
            key={event.event_id || event.event_name}
            type="button"
            role="listitem"
            className={`gallery-event-card${isSelected ? ' active' : ''}`}
            onClick={() => onSelect?.(event)}
            aria-pressed={isSelected}
          >
            <div className="gallery-event-card-header">
              {(thumbnail || hasVideoPlaceholder) && (
                <div className="gallery-event-card-thumb" aria-hidden="true">
                  {thumbnail ? (
                    <img
                      src={thumbnail}
                      alt={event.event_name}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="gallery-event-card-play">
                      <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  )}
                </div>
              )}
              <span className="gallery-event-name">{event.event_name}</span>
              {event.event_date && (
                <span className="gallery-event-date">{formatDate(event.event_date)}</span>
              )}
            </div>
            <div className="gallery-event-card-stats">
              <span className="gallery-event-stat">
                <span aria-hidden="true">📷</span>
                <span>{event.photos.length} photo{event.photos.length === 1 ? '' : 's'}</span>
              </span>
              <span className="gallery-event-stat">
                <span aria-hidden="true">🎬</span>
                <span>{event.videos.length} video{event.videos.length === 1 ? '' : 's'}</span>
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}